"use client";

import React from "react";
import { Phone, MessageCircle, Wrench, ShieldCheck, Clock, Flame, Sparkles, CircleCheck as CheckCircle2, MapPin } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
const benefitCards = [{
  title: "قطع غيار أريستون الأصلية",
  description: "توفير واستبدال قطع الغيار المعتمدة لأفران وغازات أريستون مع ضمان معتمد على القطع المستبدلة.",
  icon: ShieldCheck
}, {
  title: "صيانة منزلية فورية بالرياض",
  description: "فريق فني متخصص يصلك إلى باب المنزل في كافة أحياء الرياض لمعاينة وإصلاح العطل دون نقل الفرن.",
  icon: MapPin
}, {
  title: "إصلاح الشعلات وضعف النار",
  description: "تسليك وتعديل فونيات ومفاتيح غاز أريستون وضبط قوة الشعلات والحرارة بدقة وأمان تام.",
  icon: Flame
}, {
  title: "تنظيف وإزالة الدهون المستعصية",
  description: "تنظيف وتلميع عميق للأفران الإيطالية بمواد خاصة تزيل ترسبات الزيوت وتحافظ على لمعان الستانلس ستيل.",
  icon: Sparkles
}];
const featuresList = ["فحص كهربائي وميكانيكي شامل للفرن", "إصلاح منظم الحرارة (الثرموستات) والشواية", "صيانة مفاتيح الغاز وحساسات الأمان", "خدمة سريعة في نفس اليوم داخل مدينة الرياض"];
export default function AristonOvenHero() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  const phoneNumber = PHONE_LOCAL;
  const whatsappUrl = WHATSAPP_HREF;
  return <section id="aristonovenhero" ref={sectionRef} dir="rtl" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      {/* Decorative ambient background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -left-32 w-80 h-80 rounded-full bg-secondary/5 blur-2xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Top Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 flex flex-col items-start text-right transition-all duration-700" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)"
        }}>
            <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5 py-1 px-3 text-sm flex items-center gap-1.5 font-medium">
              <Wrench className="size-3.5" />
              <span>خدمة معتمدة داخل الرياض</span>
            </Badge>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight mb-6">
              صيانة أفران أريستون في الرياض بخبرة وضمان معتمد
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-2xl">
              نقدم في لمسة احتراف حلولاً متكاملة لصيانة أفران أريستون الإيطالية (الغاز والكهرباء) في منزلك. تشخيص دقيق للأعطال، إصلاح ضعف النار وتوقف الإشعال، وتغيير قطع الغيار التالفة على أيدي فنيين محترفين.
            </p>

            {/* Quick check points */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full mb-8">
              {featuresList.map((item, idx) => <div key={idx} data-index={idx} className="flex items-center gap-2 text-foreground text-sm font-medium">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{item}</span>
                </div>)}
            </div>

            {/* Direct CTA Buttons */}
            <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto gap-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                <a href={PHONE_TEL_HREF}>
                  <Phone className="size-4" />
                  <span>اتصل الآن: {phoneNumber}</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto gap-2 border-border hover:bg-accent text-foreground">
                <Link to={whatsappUrl} newTab>
                  <MessageCircle className="size-4 text-primary" />
                  <span>محادثة واتساب فورية</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Hero Visual Panel */}
          <div className="lg:col-span-5 relative transition-all duration-700 delay-150" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)"
        }}>
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/ariston_oven_maintenance_riyadh.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/ariston_oven_maintenance_riyadh.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/ariston_oven_maintenance_riyadh.webp-wvc-sizes" alt="فني صيانة أفران أريستون في الرياض من شركة لمسة احتراف" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6 text-white">
                <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm mb-1">
                  <Clock className="size-4" />
                  <span>خدمة فورية بنفس اليوم</span>
                </div>
                <p className="text-sm font-medium text-white/90">
                  صيانة منزلية شاملة لجميع موديلات أفران وغازات أريستون بمدينة الرياض
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Cards Section */}
        <div className="mt-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              لماذا تختار لمسة احتراف لصيانة أفران أريستون؟
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base">
              نوفر خدمة صيانة متخصصة تضمن إعادة كفاءة فرنك وأمان استخدامه اليومي دون عناء النقل.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefitCards.map((card, idx) => {
            const IconComp = card.icon;
            return <div key={idx} data-index={idx} className="bg-card border border-border rounded-xl p-6 shadow-xs hover:shadow-md transition-shadow flex flex-col items-start text-right">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <IconComp className="size-6" />
                  </div>
                  <h3 className="text-lg font-bold text-card-foreground mb-2">
                    {card.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {card.description}
                  </p>
                </div>;
          })}
          </div>
        </div>

        {/* Closing Action Band */}
        <div className="mt-16 bg-secondary text-secondary-foreground rounded-2xl p-8 sm:p-10 border border-secondary/20 shadow-lg">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-right">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2">
                هل تواجه مشكلة في فرن أو غاز أريستون الآن؟
              </h3>
              <p className="text-sm sm:text-base text-secondary-foreground/80 max-w-xl">
                تواصل مع فني لمسة احتراف بالرياض للحصول على معاينة منزلية سريعة وحل فوري لكافة الأعطال.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                <a href={PHONE_TEL_HREF}>
                  <Phone className="size-4" />
                  <span>اتصال مباشر</span>
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10 bg-transparent">
                <Link to={whatsappUrl} newTab>
                  <MessageCircle className="size-4" />
                  <span>واتساب: {phoneNumber}</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}